// ---------------------------------------------------------------------------
// The Current Order on a narrow screen.
//
// Below the `till` breakpoint there is not room for a permanent 380 px column
// beside a usable product grid, so the cart becomes two pieces:
//
//   A BAR that is always there, carrying the item count and the running total.
//   It is a button, and it is the answer to "how do I pay on a tablet" - the
//   payment action is never more than one tap away, which is the rule this
//   whole component exists to keep.
//
//   A DRAWER that slides in from the side the language reads towards. In
//   English it comes from the right, in Arabic from the left, because the cart
//   sits on the trailing edge in both - `end-0` and the RTL-aware translate
//   below do that without a second component.
//
// The bar is deliberately NOT hidden when the cart is empty. A till whose only
// payment control disappears when there is nothing in it is a till a cashier
// has to learn a rule about.
// ---------------------------------------------------------------------------

import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ShoppingCart, X } from 'lucide-react';
import clsx from 'clsx';
import { useI18n } from '../../i18n/I18nProvider';
import { useOverlayDismiss } from '../common/overlayStack';
import { formatUsd } from '../../utils/format';

/// The always-visible summary button. Rendered inside the POS column, pinned
/// to the bottom of it, so it never covers the product grid's last row.
export function CartSummaryBar({
  itemCount,
  totalUsd,
  onOpen,
}: {
  itemCount: number;
  totalUsd: string;
  onOpen: () => void;
}) {
  const { t, format } = useI18n();

  const summary =
    itemCount === 0
      ? t.pos.cartEmptySummary
      : itemCount === 1
        ? format(t.pos.cartSummaryOne, { total: formatUsd(totalUsd) })
        : format(t.pos.cartSummary, { count: itemCount, total: formatUsd(totalUsd) });

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={t.pos.openCart}
      className="flex h-touch w-full shrink-0 items-center justify-between gap-3 rounded-card border border-butcher-green bg-butcher-green px-4 text-white shadow-raised transition-colors hover:bg-deep-green till:hidden"
    >
      <span className="flex min-w-0 items-center gap-2">
        <ShoppingCart className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="truncate text-sm font-semibold">{summary}</span>
      </span>

      {/* The count again as a chip: at a glance from across the counter, the
          number is what the cashier is checking. */}
      <span className="numeric shrink-0 rounded-full bg-white/20 px-2.5 py-0.5 text-sm font-bold">
        {itemCount}
      </span>
    </button>
  );
}

/// The panel itself. `children` is the ordinary CartPanel, unchanged - the
/// drawer is a container, not a second implementation of the cart.
export function CartDrawer({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const { t } = useI18n();

  // Escape closes it, and the page behind does not scroll underneath.
  useEffect(() => {
    if (!open) return;

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Escape closes the drawer - unless a dialog is open above it, in which
  // case Escape is that dialog's to answer.
  useOverlayDismiss(open, onClose);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex till:hidden" role="dialog" aria-modal="true">
      <button
        type="button"
        className="flex-1 bg-charcoal/50"
        onClick={onClose}
        aria-label={t.pos.closeCart}
      />

      <aside
        className={clsx(
          // `w-full max-w-md`: a phone gets the whole width, a tablet gets a
          // panel. Never a fixed width that could exceed the viewport.
          'flex h-full w-full max-w-md min-w-0 flex-col bg-surface-page shadow-raised',
          'animate-fade-in',
        )}
      >
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-line bg-surface px-4 py-3">
          <h2 className="flex min-w-0 items-center gap-2 text-base font-semibold text-ink">
            <ShoppingCart className="h-5 w-5 shrink-0 text-butcher-green" aria-hidden="true" />
            <span className="truncate">{t.pos.cart}</span>
          </h2>

          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-control p-1.5 text-ink-muted hover:bg-surface-soft hover:text-ink"
            aria-label={t.pos.closeCart}
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        {/* The cart and everything above it (mode toggle, delivery panel)
            scroll together here, because on a short phone screen the delivery
            form alone is taller than the viewport. */}
        <div className="scrollbar-brand flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
          {children}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
